import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ClientDeletionContext } from "./ClientDeletionContext";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";
import { DeleteClientRequest } from "@/types/request/DeleteClientRequest";
import { ErrorResponse } from "@/types/response/ErrorResponse";

interface ClientDeletionProviderProps {
  children: React.ReactNode;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

export const ClientDeletionProvider: React.FC<ClientDeletionProviderProps> = ({
  children,
  onDeleteSuccess,
  onDeleteError
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  const deleteClientMutation = useMutation<AxiosResponse, AxiosError<ErrorResponse>, DeleteClientRequest>({
    mutationFn: (deleteClientRequest: DeleteClientRequest) => {
      return instance.delete(`/api/v2/Clients/${deleteClientRequest.clientId}?tenantId=${deleteClientRequest.tenantId}`);
    },
    onSuccess: () => {
      showToast(t('success.deleted', { entity: 'Client' }), "success");
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setDeleteSuccess(true);
      setDeleteError(false);

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    },
    onError: (error) => {
      const errorCode = error.response?.data?.code;
      const message = errorCode ? t(`errorCodes.${errorCode}`, {
        action: t('common.delete'),
        entity: t('common.client')
      }) : t('errors.somethingWentWrong');
      showToast(message, "error", undefined, t('errors.deleteFailedTitle', { entity: t('common.client') }));
      setDeleteSuccess(false);
      setDeleteError(true);

      if (onDeleteError) {
        onDeleteError();
      }
    }
  });

  const deleteClient = (request: DeleteClientRequest) => {
    setDeleteSuccess(false);
    setDeleteError(false);
    deleteClientMutation.mutate(request);
  };

  const contextValue = {
    deleteClient,
    deletingClient: deleteClientMutation.isPending,
    deleteSuccess,
    deleteError
  }

  return (
    <ClientDeletionContext.Provider value={contextValue}>
      {children}
    </ClientDeletionContext.Provider>
  );
};
